import { onDomReady } from '../utils/dom';
import { scrollToTarget } from '../utils/scrolling';

function initializeOnePageNavs() {
    if (!document.body.classList.contains('onepager')) return;

    import('purejs_onepagenav').then(({ default: OnePageNav }) => {
        // https://github.com/M-Zoldak/PureJS_OnePageNav/wiki/Configuration

        new OnePageNav({
            navLinksSelector: 'body.onepager #header .main-nav .level_1 a:not(.invisible)',
            defaultLinkActive: true,
            updateATagClass: false,
            parentsObtainingActiveClass: ['li'],
            changeOffset: 33,
        });
    });
}

/* Abschnitts-Subnav (Schmetterlingswerkstatt: Über uns / Wissenswertes / Vereinsarbeit-Übersicht) —
   leichter Scrollspy auf die Abschnitte der Seite, markiert den zuletzt erreichten Abschnitt.
   Zwei Quellen für die Links:
   1. html-Subnav (Über uns / Wissenswertes): Artikel-Anker, href="#<Artikel-ID>".
   2. Navigationsmodul „SW Vereinsarbeit - Unternavigation" (Vereinsarbeit-Übersicht): Contao listet
      die veröffentlichten Unterseiten der Ebene als Seitenlinks. Liegt zur Unterseite eine
      Vorschau-Kachel (page_teasers mit „Unterseiten verwenden") auf derselben Seite, bekommt die
      Kachel die id teaser-<alias>, der Klick scrollt zur Kachel (scrolling.js) und der Spy markiert
      sie; ohne Kachel — oder ohne JS — bleibt der Link ein normaler Seitenlink. Direktaufrufe per
      #teaser-<alias> löst base.js beim Laden über die vergebene id auf. */
function initializeSubNav() {
    const nav = document.querySelector('.subnav');
    if (!nav) return;

    const sections = new Map();

    nav.querySelectorAll('a[href^="#"]').forEach((link) => {
        const section = document.getElementById(link.hash.slice(1));
        if (section) sections.set(link, section);
    });

    const teaserLinks = [...document.querySelectorAll('.page_teaser > a[href]')];
    nav.querySelectorAll('ul a[href]:not([href^="#"])').forEach((link) => {
        const teaserLink = teaserLinks.find((candidate) => candidate.pathname === link.pathname);
        if (!teaserLink) return;

        const teaser = teaserLink.parentElement;
        if (!teaser.id) teaser.id = `teaser-${link.pathname.split('/').filter(Boolean).pop()}`;

        link.addEventListener('click', (event) => {
            event.preventDefault();
            scrollToTarget(teaser);
        });
        sections.set(link, teaser);
    });

    if (location.hash && document.getElementById(location.hash.slice(1))) {
        document.getElementById(location.hash.slice(1)).scrollIntoView();
    }

    // Mitscrollende Leiste unabhängig davon, ob Abschnitte zu den Links gefunden wurden
    initializeStickySubNav(nav);

    if (!sections.size) return;

    const links = [...sections.keys()];
    const targets = [...new Set(sections.values())];

    /* Nach einem Klick bleibt der gewählte Link markiert, bis die Scroll-Animation
       (scrolling.js, 900 ms) durch ist — sonst überschreibt der Spy die Auswahl schon
       während des Anfahrens. */
    let manualUntil = 0;

    /* Dokument-Position statt offsetTop: offsetTop misst relativ zum nächsten positionierten
       Vorfahren — bei den Vorschau-Kacheln ist das der Grid-Container, nicht das Dokument
       (28.10.: auf Tablet war darum am Seitenanfang die Kachel der zweiten Reihe markiert). */
    const topOf = (section) => section.getBoundingClientRect().top + window.scrollY;

    const setActive = () => {
        if (Date.now() < manualUntil) return;
        const pos = window.scrollY + window.innerHeight * 0.33;
        let current = targets[0];
        let currentTop = topOf(current);
        for (const section of targets) {
            const top = topOf(section);
            /* > statt >=: liegen mehrere Ziele auf gleicher Höhe (Kachel-Reihe eines Grids),
               bleibt das erste der Reihe markiert statt des letzten */
            if (top <= pos && top > currentTop) {
                current = section;
                currentTop = top;
            }
        }
        /* Gleiche Reihe: Liegt das Ziel des bereits markierten Links auf derselben Höhe wie das
           errechnete (z. B. nach Klick auf die zweite Kachel einer Reihe), bleibt die Markierung —
           sonst spränge sie nach der Klick-Animation auf die erste Kachel der Reihe zurück. */
        const activeLink = links.find((link) => link.classList.contains('active'));
        if (activeLink && Math.abs(topOf(sections.get(activeLink)) - currentTop) < 1) {
            current = sections.get(activeLink);
        }
        links.forEach((link) => link.classList.toggle('active', sections.get(link) === current));
    };

    links.forEach((link) => {
        link.addEventListener('click', () => {
            manualUntil = Date.now() + 1200;
            links.forEach((other) => other.classList.toggle('active', other === link));
        });
    });

    document.addEventListener('scroll', setActive, { passive: true });
    setActive();
}

/* Mitscrollende Subnav-Leiste: position:sticky reicht nicht (der Subnav-Artikel liegt im
   #title-image-Slot, der nach dem Titelbild endet). Stattdessen wird die Leiste per
   position:fixed (.subnav--fixed, layout/_subnav.scss) unter die Header-Unterkante gesetzt;
   der Artikel behält per expliziter Höhe seinen Platz (kein Layout-Sprung) und bleibt
   im DOM-Fluss (die :has()-Abstandsregel in _subnav.scss greift weiter).

   Kundenwunsch 18.08.: die Leiste klebt NICHT mehr dauerhaft an der Bildschirmoberkante,
   sondern folgt dem Scrollverhalten des Sticky-Headers (header.js): beim Scrollstart
   scrollt sie mit dem Inhalt aus dem View; fixiert wird nur, solange der Header selbst
   sichtbar-fixiert ist (.show/.keep).

   Kundenwunsch 20.08.: Header + Leiste bewegen sich beim Ein- UND Ausfahren als EINE
   Gruppe, in jedem Frame bündig. Zwei Bausteine:
   1. --subnav-height auf :root — auf Subnav-Seiten fährt der versteckte Sticky-Header
      um die Leistenhöhe WEITER raus (transform-Regel in layout/header/_standard.scss);
      die Unterkante der Leiste ist damit die Führungskante der Gruppe.
   2. Ein requestAnimationFrame-Loop klebt die gepinnte Leiste in JEDEM Frame an die
      Header-Unterkante. Vorher liefen zwei entkoppelte Animationen (Scroll-/
      transitionend-Events + eigene CSS-Transition auf top) — die Leiste klapperte dem
      Header sichtbar hinterher. */
function initializeStickySubNav(nav) {
    const article = nav.closest('.mod_article');
    const header = document.querySelector('#header');
    if (!article || !header) return;

    const root = document.documentElement;

    // Anker-Ziele unter Header + Leiste einscrollen lassen (scroll-padding-top, _subnav.scss)
    const setAnchorOffset = () => {
        root.style.setProperty('--subnav-offset', `${header.offsetHeight + nav.offsetHeight + 20}px`);
    };

    // Leistenhöhe für die Gruppen-transform-Regel des Headers (_standard.scss);
    // im natürlichen Fluss-Zustand messen (resize entpinnt unten vor dem Neumessen)
    const setSubnavHeight = () => {
        root.style.setProperty('--subnav-height', `${nav.offsetHeight}px`);
    };

    const unpin = () => {
        nav.classList.remove('subnav--fixed');
        nav.style.top = '';
        nav.style.width = '';
        article.style.height = '';
    };

    const update = () => {
        /* Header-Unterkante inkl. transform — bewusst OHNE Klemmung auf >= 0 (bis 20.08.
           Math.max(…, 0)): während des gemeinsamen Ein-/Ausfahrens liegt sie bis etwa
           -Leistenhöhe im Negativen, die Leiste folgt ihr dorthin (bündige Gruppe,
           fährt also selbst vollständig aus dem View bzw. in ihn hinein). */
        const headerBottom = header.getBoundingClientRect().bottom;
        const pinned = nav.classList.contains('subnav--fixed');

        /* Anheften nur, wenn der Header sichtbar-fixiert ist (.show/.keep, header.js).
           Im Normalfluss am Seitenanfang (Header ohne .sticky) scrollt die Leiste an
           ihrer natürlichen Position mit — dort ist kein Fixieren nötig. */
        const headerPinned =
            header.classList.contains('show') ||
            header.classList.contains('keep') ||
            // Anker-Animation (scrolling.js/header.js): .keep folgt einen Frame nach .sticky —
            // die Leiste muss trotzdem sofort mit anheften, sonst blitzt sie beim Einfahren
            // einen Frame hinter dem Header her
            (header.classList.contains('auto-scroll') && header.classList.contains('sticky'));

        if (headerPinned && article.getBoundingClientRect().top < headerBottom) {
            if (!pinned) {
                article.style.height = `${article.offsetHeight}px`; // Platz freihalten
                nav.classList.add('subnav--fixed');
                // clientWidth (ohne Scrollbar) = Breite des nicht-fixierten Zustands → kein Versatz,
                // das padding-inline der Leiste rechnet die Links damit wieder auf Content-Position
                // (bei resize wird entpinnt und hier neu gemessen)
                nav.style.width = `${root.clientWidth}px`;
            }
            nav.style.top = `${headerBottom}px`;
        } else if (pinned && header.classList.contains('sticky') && headerBottom > -nav.offsetHeight) {
            /* Der Header fährt gerade aus (.hide, header.js) bzw. seine Transition läuft
               noch: weiter in jedem Frame an die Unterkante kleben — die Gruppe fährt
               vollständig gemeinsam raus (der Header taucht dank --subnav-height tief
               genug ab, headerBottom endet unterhalb von -Leistenhöhe). Entpinnt wird
               erst, wenn die Leiste komplett draußen ist — kein sichtbarer Sprung.
               Ist der Header dagegen gar nicht mehr .sticky (Sprung an den Seitenanfang:
               header.js räumt alle Klassen ab, es läuft keine Transition), greift sofort
               der unpin()-Zweig — die Leiste steht dann wieder an ihrer natürlichen
               Position direkt unter dem Header. */
            nav.style.top = `${headerBottom}px`;
        } else if (pinned) {
            unpin();
        }
    };

    /* rAF-Loop statt Scroll-Listener + transitionend (bis 20.08.): der Header bewegt
       sich per CSS-Transition, Events liefern seine Position nur punktuell — die Leiste
       lief sichtbar hinterher. Der Loop läuft nur auf Subnav-Seiten (diese Funktion wird
       nur bei vorhandener .subnav erreicht) und kostet 1 getBoundingClientRect() pro
       Frame. Nebeneffekt: rAF-Callbacks laufen NACH allen Scroll-Handlern desselben
       Frames — update() sieht damit immer den AKTUELLEN Klassenzustand von header.js.
       Das frühere Listener-Ordering-Problem (document- vs. window-Listener: die Leiste
       blieb beim Sprung an den Seitenanfang unsichtbar-gepinnt hängen, weil der letzte
       Handler-Lauf noch '.hide' sah) kann so konstruktionsbedingt nicht mehr auftreten. */
    const tick = () => {
        update();
        requestAnimationFrame(tick);
    };

    window.addEventListener('resize', () => {
        unpin(); // neu messen — die Leiste wrappt auf schmalen Screens mehrzeilig
        setAnchorOffset();
        setSubnavHeight();
        update();
    });

    setAnchorOffset();
    setSubnavHeight();
    requestAnimationFrame(tick);
}

onDomReady(() => {
    initializeOnePageNavs();
    initializeSubNav();
});
